import {
  isStrongPassword,
  isValidDob,
  isValidEmail,
  isValidPhoneNumber,
  isValidUrl,
  isValidUsername,
  isValidZip,
} from '../utils/validation';
import ApplicationController from './application_controller';

/**
 * Generic form validation controller
 *
 * Usage:
 * <form data-controller="form-validation" data-action="submit->form-validation#validateOnSubmit">
 *   <div>
 *     <input
 *       name="email"
 *       data-form-validation-target="input"
 *       data-validate="email"
 *       data-validate-on="blur"
 *       data-validate-required="true"
 *       data-error-target="email-error"
 *     />
 *     <span id="email-error" class="hidden text-red-500">Invalid email</span>
 *   </div>
 *
 *   <button type="submit">Submit</button>
 * </form>
 *
 * Input data attributes:
 * - data-validate: Type of validation (email, phone, password, username, url, zip, dob, required)
 * - data-validate-on: When to validate (blur, input, change) - defaults to "blur"
 * - data-validate-required: Whether field is required (true/false) - defaults to false
 * - data-validate-message: Custom error message (optional)
 * - data-error-target: ID of the error message element (optional, will try to find nearby)
 * - data-validate-min-length: Minimum length for text inputs
 * - data-validate-max-length: Maximum length for text inputs
 * - data-validate-pattern: Custom regex pattern (e.g., "^\d{5}$")
 *
 * Multiple validators:
 * - data-validate="required,email" - will validate both required and email
 */
export default class extends ApplicationController {
  static targets = ['input', 'submitButton'];

  connect() {
    this.setupValidation();
    this.updateButtonState();
  }

  setupValidation() {
    // For each input with validation
    this.inputTargets.forEach((input) => {
      const validateOn = input.dataset.validateOn || 'blur';
      const events = validateOn.split(',').map((e) => e.trim());

      events.forEach((event) => {
        input.addEventListener(event, () => {
          this.validateInput(input);
          this.updateButtonState();
        });
      });

      // Mark field as touched on focus for better UX (except radio/checkbox)
      if (input.type !== 'radio' && input.type !== 'checkbox') {
        input.addEventListener('focus', () => {
          input.dataset.touched = 'true';
        });
      }

      // For radio buttons, mark all in group as touched when any is clicked
      if (input.type === 'radio') {
        input.addEventListener('change', () => {
          const radios = document.querySelectorAll(`input[name="${input.name}"]`);
          radios.forEach((radio) => {
            radio.dataset.touched = 'true';
          });
          this.updateButtonState();
        });
      }

      // For checkboxes, update button state on change
      if (input.type === 'checkbox') {
        input.addEventListener('change', () => {
          this.updateButtonState();
        });
      }

      // For text inputs, also update on input for real-time feedback
      if (input.type !== 'radio' && input.type !== 'checkbox') {
        input.addEventListener('input', () => {
          this.updateButtonState();
        });
      }
    });
  }

  /**
   * Validate a single input element
   * @param {HTMLInputElement} input - The input element to validate
   * @returns {boolean} - Whether the input is valid
   */
  validateInput(input) {
    // Skip validation if field hasn't been touched yet (unless forcing validation)
    if (!input.dataset.touched && !input.dataset.forceValidate) {
      return true;
    }

    const value = input.value.trim();
    const validators = (input.dataset.validate || '').split(',').map((v) => v.trim());
    
    const isRequired = input.dataset.validateRequired === 'true' || input.required;
    const customMessage = input.dataset.validateMessage;

    // Special handling for radio buttons
    if (input.type === 'radio') {
      const isChecked = document.querySelector(`input[name="${input.name}"]:checked`);

      if (isRequired && !isChecked) {
        this.showError(input, customMessage || 'Please select an option');
        return false;
      }

      if (isChecked) {
        this.clearError(input);
        return true;
      }
    }

    // Check required first
    if (isRequired && !value) {
      this.showError(input, customMessage || 'This field is required');
      return false;
    }

    // If not required and empty, clear errors and pass
    if (!isRequired && !value) {
      this.clearError(input);
      return true;
    }

    // Run through each validator
    for (const validatorType of validators) {
      if (!validatorType || validatorType === 'required') continue;

      const result = this.runValidator(validatorType, value, input);

      if (!result.valid) {
        this.showError(input, customMessage || result.message);
        return false;
      }
    }

    // All validations passed
    this.clearError(input);
    return true;
  }

  /**
   * Check if all form inputs are valid
   * @returns {boolean} - Whether all inputs are valid
   */
  isFormValid() {
    let allValid = true;

    this.inputTargets.forEach((input) => {
      const value = input.value.trim();
      const validators = (input.dataset.validate || '').split(',').map((v) => v.trim());
      const isRequired = validators.includes('required') || input.dataset.validateRequired === 'true' || input.required;

      // For checkboxes
      if (input.type === 'checkbox') {
        if (isRequired && !input.checked) {
          allValid = false;
        }
        return;
      }

      // For radio buttons
      if (input.type === 'radio') {
        const isChecked = document.querySelector(`input[name="${input.name}"]:checked`);
        if (isRequired && !isChecked) {
          allValid = false;
        }
        return;
      }

      // For other inputs - must have value and pass all validators
      if (isRequired && !value) {
        allValid = false;
        return;
      }

      // If not required and empty, it's valid
      if (!isRequired && !value) {
        return;
      }

      // Run validators
      for (const validatorType of validators) {
        if (!validatorType || validatorType === 'required') continue;

        const result = this.runValidator(validatorType, value, input);
        if (!result.valid) {
          allValid = false;
          return;
        }
      }
    });

    return allValid;
  }

  /**
   * Update submit button state based on form validity
   */
  updateButtonState() {
    if (!this.hasSubmitButtonTarget) return;

    const isValid = this.isFormValid();

    this.submitButtonTarget.setAttribute('aria-disabled', String(!isValid));

    if (isValid) {
      // Enable button - teal background
      this.submitButtonTarget.classList.remove('cursor-not-allowed', 'bg-[#EAEAEA]', 'text-[#727374]');
      this.submitButtonTarget.classList.add('bg-[#0FEED7]', 'text-[#020D19]', 'hover:bg-[#0DDDC6]');
      this.submitButtonTarget.disabled = false;
    } else {
      // Disable button - gray background
      this.submitButtonTarget.classList.remove('bg-[#0FEED7]', 'text-[#020D19]', 'hover:bg-[#0DDDC6]');
      this.submitButtonTarget.classList.add('cursor-not-allowed', 'bg-[#EAEAEA]', 'text-[#727374]');
      this.submitButtonTarget.disabled = true;
    }
  }

  /**
   * Run a specific validator
   * @param {string} type - The validator type
   * @param {string} value - The value to validate
   * @param {HTMLInputElement} input - The input element (for accessing data attributes)
   * @returns {Object} - { valid: boolean, message: string }
   */
  runValidator(type, value, input) {
    const validators = {
      email: {
        fn: isValidEmail,
        message: 'Please enter a valid email address',
      },
      phone: {
        fn: isValidPhoneNumber,
        message: 'Please enter a valid phone number',
      },
      password: {
        fn: isStrongPassword,
        message: 'Password must be at least 12 characters and cannot be entirely numbers',
      },
      username: {
        fn: isValidUsername,
        message: 'Username must be 3-20 characters (letters, numbers, . _ -)',
      },
      url: {
        fn: isValidUrl,
        message: 'Please enter a valid URL',
      },
      zip: {
        fn: isValidZip,
        message: 'Please enter a valid zip code',
      },
      dob: {
        fn: isValidDob,
        message: 'You must be at least 18 years old',
      },
    };

    // Handle built-in validators
    if (validators[type]) {
      const { fn, message } = validators[type];
      return {
        valid: fn(value),
        message,
      };
    }

    // Handle custom pattern validation
    if (type === 'pattern') {
      const pattern = input.dataset.validatePattern;
      if (pattern) {
        const regex = new RegExp(pattern);
        return {
          valid: regex.test(value),
          message: 'Invalid format',
        };
      }
    }

    // Handle min/max length
    if (type === 'minLength') {
      const minLength = parseInt(input.dataset.validateMinLength, 10);
      return {
        valid: value.length >= minLength,
        message: `Must be at least ${minLength} characters`,
      };
    }

    if (type === 'maxLength') {
      const maxLength = parseInt(input.dataset.validateMaxLength, 10);
      return {
        valid: value.length <= maxLength,
        message: `Must be no more than ${maxLength} characters`,
      };
    }

    // Handle unique validation
    if (type === 'unique') {
      const validationUniqueUrl = input.dataset.validateUniqueUrl;
      const validationUniqueMessage = input.dataset.validateUniqueMessage;
      if (!validationUniqueMessage) {
        validationUniqueMessage = 'This value must be unique';
      }
      if (validationUniqueUrl) {
        fetch(`${validationUniqueUrl}?value=${encodeURIComponent(value)}`)
          .then(response => response.json())
          .then(data => {
            if (data.exists) {
              this.showError(input, validationUniqueMessage);
              return { valid: false, message: validationUniqueMessage };
            } else {
              this.clearError(input);
              return { valid: true, message: '' };
            }
          })
          .catch(() => {
            this.showError(input, 'Error validating uniqueness');
            return { valid: false, message: 'Error validating uniqueness' };
          });
      } else {  
        return { valid: true, message: '' };
      }
    }

    // Unknown validator - log warning and pass
    console.warn(`Unknown validator type: ${type}`);
    return { valid: true, message: '' };
  }

  /**
   * Show error message for an input
   * @param {HTMLInputElement} input - The input element
   * @param {string} message - The error message to display
   */
  showError(input, message) {
    const errorElement = this.findErrorElement(input);

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');
    }

    // Add error styling to input - preserve original border, just add red
    input.classList.add('border-red-500');
    input.setAttribute('aria-invalid', 'true');

    // Store original border classes if not already stored
    if (!input.dataset.originalBorder) {
      const borderClasses = Array.from(input.classList).filter(c =>
        c.startsWith('border-') && c !== 'border-red-500'
      );
      input.dataset.originalBorder = borderClasses.join(' ');
    }

    // Hide success indicator for username field
    if (input.name === 'username') {
      const successIcon = document.querySelector('[data-username-ok]');
      if (successIcon) {
        successIcon.style.opacity = '0';
      }
    }
  }

  /**
   * Clear error message for an input
   * @param {HTMLInputElement} input - The input element
   */
  clearError(input) {
    const errorElement = this.findErrorElement(input);

    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.add('hidden');
    }

    // Remove error styling from input and restore original
    input.classList.remove('border-red-500');
    input.setAttribute('aria-invalid', 'false');

    // Restore original border classes if they were stored
    if (input.dataset.originalBorder) {
      const originalClasses = input.dataset.originalBorder.split(' ');
      originalClasses.forEach(cls => {
        if (cls) input.classList.add(cls);
      });
    }

    // Show success indicator for username field (optional checkmark)
    if (input.name === 'username') {
      const successIcon = document.querySelector('[data-username-ok]');
      if (successIcon && input.value.trim()) {
        successIcon.style.opacity = '1';
      }
    }
  }

  /**
   * Find the error element for an input
   * Looks for data-error-target attribute or nearby error elements
   * @param {HTMLInputElement} input - The input element
   * @returns {HTMLElement|null} - The error element
   */
  findErrorElement(input) {
    // Check for explicit error target ID
    const errorTargetId = input.dataset.errorTarget;
    if (errorTargetId) {
      return document.getElementById(errorTargetId);
    }

    // For radio buttons, look for error element in parent container
    if (input.type === 'radio') {
      // Look for error element with matching name in the parent container
      let container = input.closest('[data-add-property-form-target], .flex, .grid, fieldset');
      if (container) {
        const errorFor = container.querySelector(`[data-error-for="${input.name}"]`);
        if (errorFor) return errorFor;

        // Also check for ID-based error
        const errorById = container.querySelector(`#${input.name}_error`);
        if (errorById) return errorById;
      }
    }

    // Check for sibling error element with data-error-for attribute
    const errorFor = input.parentElement?.querySelector(`[data-error-for="${input.name}"]`);
    if (errorFor) {
      return errorFor;
    }

    // Look for nearby element with common error classes
    const nearbyError = input.parentElement?.querySelector('.error-message, .text-red-500, [role="alert"]');
    if (nearbyError) {
      return nearbyError;
    }

    // Look for next sibling that might be an error
    let sibling = input.nextElementSibling;
    while (sibling) {
      if (sibling.classList.contains('hidden') || sibling.classList.contains('text-red-500')) {
        return sibling;
      }
      sibling = sibling.nextElementSibling;
    }

    return null;
  }

  /**
   * Validate all inputs in the form (called on submit)
   * @param {Event} event - The submit event
   */
  validateOnSubmit(event) {
    let isValid = true;

    // Mark all inputs as touched and force validation
    this.inputTargets.forEach((input) => {
      input.dataset.touched = 'true';
      input.dataset.forceValidate = 'true';

      if (!this.validateInput(input)) {
        isValid = false;
      }

      delete input.dataset.forceValidate;
    });

    // Prevent form submission if invalid
    if (!isValid) {
      event.preventDefault();
      event.stopPropagation();

      // Focus first invalid input
      const firstInvalid = this.inputTargets.find((input) => input.getAttribute('aria-invalid') === 'true');
      if (firstInvalid) {
        firstInvalid.focus();
      }
    }

    return isValid;
  }

  /**
   * Manually validate entire form (can be called from other controllers)
   * @returns {boolean} - Whether the form is valid
   */
  validate() {
    let isValid = true;

    this.inputTargets.forEach((input) => {
      input.dataset.touched = 'true';
      input.dataset.forceValidate = 'true';

      if (!this.validateInput(input)) {
        isValid = false;
      }

      delete input.dataset.forceValidate;
    });

    return isValid;
  }

  /**
   * Clear all errors in the form
   */
  clearAllErrors() {
    this.inputTargets.forEach((input) => {
      this.clearError(input);
      delete input.dataset.touched;
    });
  }
}
